/**
 * 分镜相关 API Hooks
 */
import { useCallback } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { storyboardApi } from '@/services/api';
import type { Storyboard, CreateStoryboardDto, UpdateStoryboardDto } from '@/types';
import type {
  PaginatedStoryboardList,
  StoryboardBatchSavePayload,
} from '@/services/api/storyboards';
import type { ScreenplayScene } from '@/types/screenplay';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { shouldKeepStoryboardWindowPlaceholderData } from '@/lib/storyboard-board/project-switch';
import { invalidateOnStoryboardDelete } from '@/lib/query-invalidation';
import { invalidateEpisodeLinkedResourceQueries } from '@/hooks/api/useResourceLibrary';
import { screenplayKeys } from '@/hooks/api/useScreenplays';
import { invalidateStoryboardBoardQueries } from '@/hooks/api/useStoryboardBoard';
import { invalidateStoryboardReviewStatus } from './storyboardReviewKeys';

// Query Keys
function resolveIncludeItemsOption(includeItems?: boolean) {
  return includeItems ?? false;
}

export const storyboardKeys = {
  all: ['storyboards'] as const,
  lists: () => [...storyboardKeys.all, 'list'] as const,
  listRoot: (projectId: string) => [...storyboardKeys.lists(), projectId] as const,
  list: (projectId: string, options?: { includeItems?: boolean }) =>
    [
      ...storyboardKeys.listRoot(projectId),
      {
        includeItems: resolveIncludeItemsOption(options?.includeItems),
      },
    ] as const,
  infinite: (projectId: string, pageSize: number) =>
    [...storyboardKeys.all, 'infinite', projectId, pageSize] as const,
  details: () => [...storyboardKeys.all, 'detail'] as const,
  detail: (id: string) => [...storyboardKeys.details(), id] as const,
  scene: (id: string) => [...storyboardKeys.all, 'scene', id] as const,
};

const DEFAULT_STORYBOARD_PAGE_SIZE = 100;

const createStoryboardWindowPlaceholderData =
  <T>(projectId: string | null) =>
  (previousData: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined) => {
    const previousProjectId =
      typeof previousQuery?.queryKey?.[2] === 'string' ? previousQuery.queryKey[2] : null;

    return shouldKeepStoryboardWindowPlaceholderData(previousProjectId, projectId)
      ? keepPreviousData(previousData)
      : undefined;
  };

/**
 * 获取项目的所有分镜
 */
export function useStoryboards(
  projectId: string | null,
  options?: Pick<UseQueryOptions<Storyboard[]>, 'enabled'> & { includeItems?: boolean }
) {
  return useQuery({
    queryKey: storyboardKeys.list(projectId!, { includeItems: options?.includeItems }),
    queryFn: ({ signal }) =>
      storyboardApi.getByProjectId(
        projectId!,
        {
          includeItems: options?.includeItems,
        },
        signal
      ),
    enabled: (options?.enabled ?? true) && !!projectId,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

export function useStoryboardWindow(
  projectId: string | null,
  startIndex: number,
  endIndex: number,
  enabled = true
) {
  return useQuery({
    queryKey: [...storyboardKeys.all, 'window', projectId, startIndex, endIndex] as const,
    enabled: !!projectId && enabled && startIndex >= 0 && endIndex >= startIndex,
    queryFn: () => storyboardApi.listByWindow(projectId!, startIndex, endIndex, false),
    select: (result) => result.items,
    placeholderData: createStoryboardWindowPlaceholderData(projectId),
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * 获取项目分镜分页列表（用于无限滚动）
 */
export function useInfiniteStoryboards(
  projectId: string | null,
  pageSize = DEFAULT_STORYBOARD_PAGE_SIZE,
  options?: { includeItems?: boolean }
) {
  const includeItems = options?.includeItems ?? false;
  return useInfiniteQuery<PaginatedStoryboardList>({
    queryKey: [...storyboardKeys.infinite(projectId!, pageSize), { includeItems }] as const,
    enabled: !!projectId,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      storyboardApi.listByProjectId(projectId!, {
        page: Number(pageParam),
        pageSize,
        includeItems,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

export function useStoryboardAnchorWindow(
  projectId: string | null,
  storyboardId: string | null,
  pageSize = DEFAULT_STORYBOARD_PAGE_SIZE
) {
  return useQuery({
    queryKey: [...storyboardKeys.all, 'anchor', projectId, storyboardId, pageSize] as const,
    enabled: !!projectId && !!storyboardId,
    queryFn: () => storyboardApi.listByAnchor(projectId!, storyboardId!, pageSize, false),
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * 获取单个分镜
 */
export function useStoryboard(id: string | null) {
  return useQuery({
    queryKey: storyboardKeys.detail(id!),
    queryFn: () => storyboardApi.getById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
    initialData: () => {
      // 尝试从所有已缓存的列表中查找
      // 这里简化处理，假设我们知道 projectId 或者是最近访问的
      // 由于这里只有 id，没有 projectId，很难精确定位是哪个 list key。
      // 但我们可以遍历所有 query cache，或者放弃 initialData 如果没有 context。
      // 实际上通常在调用 useStoryboard 的组件里，我们可能有 context。
      // 无论如何，最简单的优化是如果有 projectId 上下文。
      // 既然这里没有 projectId 参数，我们暂不实现跨级 initialData，
      // 或者需要修改 hook 签名接受 projectId (可选)。
      return undefined;
    },
  });
}

/**
 * 创建分镜
 */
export function useCreateStoryboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStoryboardDto) => storyboardApi.create(data),
    onSuccess: (createdStoryboard, variables) => {
      if (!createdStoryboard?.projectId) return;
      const projectId = createdStoryboard.projectId.toString();
      clearStoryboardBoardCache(projectId);
      invalidateStoryboardReviewStatus(queryClient, projectId);
      // 精准合并到 list 缓存（StoryboardList / usePreviewData / ParseFlow 等同源），避免 invalidate
      // 触发全量 refetch 导致 ParseFlow 在自动保存期间被空数据覆盖。
      queryClient.setQueriesData<Storyboard[]>(
        { queryKey: storyboardKeys.listRoot(projectId) },
        (old) => {
          const nextCreated =
            typeof variables.orderIndex === 'number'
              ? { ...createdStoryboard, orderIndex: variables.orderIndex }
              : createdStoryboard;
          if (!old) return [nextCreated];
          const withoutCreated = old.filter((s) => s.id !== nextCreated.id);
          if (typeof variables.orderIndex === 'number') {
            const targetIndex = Math.max(
              0,
              Math.min(variables.orderIndex - 1, withoutCreated.length)
            );
            const next = [...withoutCreated];
            next.splice(targetIndex, 0, nextCreated);
            return next.map((storyboard, index) => ({
              ...storyboard,
              orderIndex: index + 1,
            }));
          }
          return [...withoutCreated, nextCreated].sort(
            (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
          );
        }
      );
    },
  });
}

/**
 * 更新分镜
 */
export function useUpdateStoryboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateStoryboardDto;
      skipBoardInvalidation?: boolean;
      skipReviewStatusInvalidation?: boolean;
    }) => storyboardApi.update(id, data),
    onSuccess: (updatedStoryboard, variables) => {
      if (updatedStoryboard) {
        const projectId = updatedStoryboard.projectId.toString();
        if (!variables.skipReviewStatusInvalidation) {
          invalidateStoryboardReviewStatus(queryClient, projectId);
        }
        // 更新单个分镜缓存
        queryClient.setQueryData<Storyboard | null>(
          storyboardKeys.detail(updatedStoryboard.id.toString()),
          updatedStoryboard
        );
        // 更新列表中的分镜
        queryClient.setQueriesData<Storyboard[]>(
          { queryKey: storyboardKeys.listRoot(projectId) },
          (old) => old?.map((s) => (s.id === updatedStoryboard.id ? updatedStoryboard : s))
        );

        // 批量保存场景（如 ParseFlowDialog 关闭时）跳过 board invalidation，由调用方统一刷新
        if (!variables.skipBoardInvalidation) {
          clearStoryboardBoardCache(projectId);
          queryClient.invalidateQueries({
            queryKey: ['storyboardBoard', 'skeleton', projectId],
          });
          queryClient.invalidateQueries({
            queryKey: ['storyboardBoard', 'list', projectId],
          });
          queryClient.invalidateQueries({
            queryKey: ['storyboardBoard', 'paginated', projectId],
          });
        } else {
          // board invalidation skipped (skipBoardInvalidation=true)
        }

        // detail 和 list 已通过 setQueryData/setQueriesData 精准更新；不再单独失效 infinite。
      }
    },
  });
}

/**
 * 删除分镜
 */
export function useBatchSaveStoryboards() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      skipReviewStatusInvalidation: _skipReviewStatusInvalidation,
      ...payload
    }: StoryboardBatchSavePayload & { skipReviewStatusInvalidation?: boolean }) =>
      storyboardApi.batchSave(payload),
    onSuccess: (result, variables) => {
      result.items.forEach((item) => {
        void queryClient.invalidateQueries({ queryKey: storyboardKeys.detail(item.storyboardId) });
      });

      const projectId = variables.projectId;
      clearStoryboardBoardCache(projectId);
      if (!variables.skipReviewStatusInvalidation) {
        invalidateStoryboardReviewStatus(queryClient, projectId);
      }
      void queryClient.invalidateQueries({ queryKey: storyboardKeys.listRoot(projectId) });
    },
  });
}

export function useDeleteStoryboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }) => storyboardApi.delete(id),
    onMutate: async ({ id, projectId }) => {
      // 取消正在进行的请求
      await queryClient.cancelQueries({ queryKey: storyboardKeys.listRoot(projectId) });
      await queryClient.cancelQueries({
        queryKey: [...storyboardKeys.all, 'window', projectId],
      });
      await queryClient.cancelQueries({
        queryKey: [...storyboardKeys.all, 'infinite', projectId],
      });

      // 获取之前的数据用于回滚
      const previousStoryboardLists = queryClient.getQueriesData<Storyboard[]>({
        queryKey: storyboardKeys.listRoot(projectId),
      });
      const previousWindowData = queryClient.getQueriesData<PaginatedStoryboardList>({
        queryKey: [...storyboardKeys.all, 'window', projectId],
      });
      const previousInfiniteData = queryClient.getQueriesData<
        InfiniteData<PaginatedStoryboardList>
      >({
        queryKey: [...storyboardKeys.all, 'infinite', projectId],
      });

      // 乐观更新 - 立即从所有列表变体中移除
      queryClient.setQueriesData<Storyboard[]>(
        { queryKey: storyboardKeys.listRoot(projectId) },
        (old) => old?.filter((s) => s.id !== id)
      );

      // 乐观更新 - 从窗口查询中移除（看板 bucket 数据来源）
      queryClient.setQueriesData<PaginatedStoryboardList>(
        { queryKey: [...storyboardKeys.all, 'window', projectId] },
        (old) =>
          old
            ? {
                ...old,
                items: old.items.filter((s) => s.id !== id),
                total: Math.max(0, old.total - 1),
              }
            : old
      );

      // 乐观更新 - 从无限分页查询中移除（StoryboardList 数据来源）
      queryClient.setQueriesData<InfiniteData<PaginatedStoryboardList>>(
        { queryKey: [...storyboardKeys.all, 'infinite', projectId] },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((s) => s.id !== id),
                  total: Math.max(0, page.total - 1),
                })),
              }
            : old
      );

      return { previousStoryboardLists, previousWindowData, previousInfiniteData };
    },
    onError: (_error, _variables, context) => {
      // 发生错误时回滚
      if (context?.previousStoryboardLists) {
        context.previousStoryboardLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousWindowData) {
        context.previousWindowData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousInfiniteData) {
        context.previousInfiniteData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (_, { id, projectId }) => {
      void invalidateEpisodeLinkedResourceQueries(queryClient);
      invalidateStoryboardReviewStatus(queryClient, projectId);
      invalidateOnStoryboardDelete(id, projectId);
    },
  });
}

/**
 * 复制分镜
 */
export function useDuplicateStoryboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => storyboardApi.duplicate(id),
    onSuccess: (newStoryboard) => {
      if (newStoryboard) {
        const projectId = newStoryboard.projectId.toString();
        clearStoryboardBoardCache(projectId);
        invalidateStoryboardReviewStatus(queryClient, projectId);
        // 失效列表查询以获取最新数据（因为排序可能变化）
        queryClient.invalidateQueries({
          queryKey: storyboardKeys.listRoot(projectId),
        });
      }
    },
  });
}

/**
 * 重新排序分镜
 */
export function useReorderStoryboards() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      orderedIds,
    }: {
      projectId: string;
      orderedIds: string[];
      skipBoardInvalidation?: boolean;
    }) => storyboardApi.reorder(projectId, orderedIds),
    onMutate: async ({ projectId, orderedIds }) => {
      // 取消正在进行的请求
      await queryClient.cancelQueries({
        queryKey: storyboardKeys.listRoot(projectId),
      });

      // 获取之前的数据
      const previousStoryboardLists = queryClient.getQueriesData<Storyboard[]>({
        queryKey: storyboardKeys.listRoot(projectId),
      });

      // 乐观更新 - 重新排序
      const primaryStoryboardList = previousStoryboardLists[0]?.[1];
      if (primaryStoryboardList) {
        const reordered = orderedIds
          .map((id, index) => {
            const storyboard = primaryStoryboardList.find((s) => s.id.toString() === id);
            if (storyboard) {
              return { ...storyboard, orderIndex: index + 1 };
            }
            return null;
          })
          .filter((s): s is Storyboard => s !== null);

        queryClient.setQueriesData<Storyboard[]>(
          { queryKey: storyboardKeys.listRoot(projectId) },
          reordered
        );
      }

      return { previousStoryboardLists };
    },
    onError: (_error, _variables, context) => {
      // 发生错误时回滚
      if (context?.previousStoryboardLists) {
        context.previousStoryboardLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (_data, variables) => {
      const { projectId, skipBoardInvalidation } = variables;
      clearStoryboardBoardCache(projectId);
      invalidateStoryboardReviewStatus(queryClient, projectId);
      // 重排成功后，强制刷新看板相关缓存（骨架/完整/分页）
      // 否则 UI 会继续使用旧的 skeleton 位置，表现为”排序成功但块还在原位”
      if (!skipBoardInvalidation) {
        queryClient.invalidateQueries({
          queryKey: ['storyboardBoard', 'skeleton', projectId],
        });
        queryClient.invalidateQueries({
          queryKey: ['storyboardBoard', 'list', projectId],
        });
        queryClient.invalidateQueries({
          queryKey: ['storyboardBoard', 'paginated', projectId],
        });
      }

      // 同步刷新分镜列表，确保 orderIndex 与服务端一致
      queryClient.invalidateQueries({
        queryKey: storyboardKeys.all,
      });
    },
  });
}

export function useStoryboardScene(storyboardId: string | null) {
  return useQuery<ScreenplayScene | null>({
    queryKey: storyboardKeys.scene(storyboardId!),
    queryFn: () => storyboardApi.getScene(storyboardId!),
    enabled: !!storyboardId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

// ─── 缓存操作辅助函数（供其他 hook 调用，勿直接 import keys）───────────────

/**
 * 失活分镜列表缓存。
 * 在任何影响分镜列表的操作后调用（创建/删除/重排分镜，以及删除元素/资源时）。
 */
export function invalidateStoryboards(queryClient: QueryClient, projectId?: string): void {
  if (projectId) {
    void queryClient.invalidateQueries({
      queryKey: storyboardKeys.listRoot(projectId),
    });
  } else {
    void queryClient.invalidateQueries({
      queryKey: storyboardKeys.lists(),
    });
  }
}

/** 剧本 AI 拆解完成后，同步失活分镜/剧本/资源等相关查询（发起人轮询与 SSE 通知副作用共用）。 */
export function refreshScreenplayParseResultQueries(
  queryClient: QueryClient,
  options: {
    storyboardProjectId: string;
    screenplayId?: string | null;
    relatedProjectIds?: string[];
    resultType?: 'asset' | 'storyboard' | 'all';
  }
): Promise<void> {
  const { storyboardProjectId, screenplayId, relatedProjectIds = [], resultType = 'all' } = options;
  const projectIds = [...new Set([storyboardProjectId, ...relatedProjectIds].filter(Boolean))];
  const affectedStoryboardIds = new Set<string>();
  if (resultType !== 'asset') {
    for (const projectId of projectIds) {
      for (const [, boards] of queryClient.getQueriesData<Storyboard[]>({
        queryKey: storyboardKeys.listRoot(projectId),
      })) {
        for (const board of boards ?? []) affectedStoryboardIds.add(board.id);
      }
    }
  }
  const resourceProjectIds = new Set(projectIds);
  // 资产库归主项目；从已加载的项目详情补齐范围，不为刷新额外发请求。
  for (const [, project] of queryClient.getQueriesData<{
    id?: string;
    parentProjectId?: string;
  }>({
    queryKey: ['projects', 'detail'],
  })) {
    if (project?.id && resourceProjectIds.has(project.id) && project.parentProjectId) {
      resourceProjectIds.add(project.parentProjectId);
    }
  }
  const tasks: Promise<void>[] = [];
  if (screenplayId) {
    tasks.push(queryClient.invalidateQueries({ queryKey: screenplayKeys.detail(screenplayId) }));
    tasks.push(
      queryClient.invalidateQueries({ queryKey: screenplayKeys.parseStatus(screenplayId) })
    );
  }
  for (const projectId of projectIds) {
    tasks.push(queryClient.invalidateQueries({ queryKey: screenplayKeys.listRoot(projectId) }));
    if (resultType !== 'asset') {
      clearStoryboardBoardCache(projectId);
      tasks.push(queryClient.invalidateQueries({ queryKey: storyboardKeys.listRoot(projectId) }));
      tasks.push(
        invalidateStoryboardBoardQueries(queryClient, projectId).then(() => {
          removeStoryboardWindowQueries(queryClient, projectId);
        })
      );
    }
  }
  if (resultType !== 'asset' && screenplayId) {
    tasks.push(queryClient.invalidateQueries({ queryKey: screenplayKeys.scenes(screenplayId) }));
  }
  if (resultType !== 'asset') {
    for (const projectId of resourceProjectIds) {
      tasks.push(
        queryClient.invalidateQueries({
          queryKey: [...screenplayKeys.dialogDirectoryRoot(), projectId],
        })
      );
    }
  }
  // 同一查询只匹配一个精准 predicate；不再清空全局资源/媒体缓存。
  tasks.push(
    queryClient.invalidateQueries({
      predicate: ({ queryKey: key }) => {
        if (key[0] === 'resource-library') return resourceProjectIds.has(String(key[3]));
        if (key[0] === 'project-elements') return resourceProjectIds.has(String(key[2]));
        if (key[0] === 'element-details') return resourceProjectIds.has(String(key[3]));
        if (key[0] === 'project-element-tags') return resourceProjectIds.has(String(key[1]));
        if (
          ['storyboard-images', 'storyboard-videos', 'storyboardDialogues'].includes(String(key[0]))
        ) {
          return affectedStoryboardIds.has(String(key[1]));
        }
        if (key[0] === 'dialogueVoiceovers' && key[1] === 'storyboard') {
          return affectedStoryboardIds.has(String(key[2]));
        }
        return false;
      },
    })
  );
  return Promise.all(tasks).then(() => undefined);
}

/**
 * 读取所有分镜列表缓存数据（用于初始数据种子或乐观更新前备份）。
 * 返回 [queryKey, data][] 元组数组。
 */
export function getStoryboardListsData(
  queryClient: QueryClient
): [QueryKey, Storyboard[] | undefined][] {
  return queryClient.getQueriesData<Storyboard[]>({
    queryKey: storyboardKeys.lists(),
  });
}

/**
 * 返回一个稳定回调，用于失活指定项目的分镜列表缓存。
 * 供 `.tsx` 组件调用，避免直接使用 `useQueryClient`。
 */
export function useInvalidateStoryboardList() {
  const queryClient = useQueryClient();
  return useCallback(
    (projectId: string) => {
      void queryClient.invalidateQueries({ queryKey: storyboardKeys.listRoot(projectId) });
    },
    [queryClient]
  );
}

export function useRefreshStoryboardsAfterParseFlowApply() {
  const queryClient = useQueryClient();
  return useCallback(
    (projectId: string) => {
      clearStoryboardBoardCache(projectId);
      void queryClient.invalidateQueries({ queryKey: storyboardKeys.listRoot(projectId) });
      void invalidateStoryboardBoardQueries(queryClient, projectId);
    },
    [queryClient]
  );
}

export function useRefreshScreenplayParseResultQueries() {
  const queryClient = useQueryClient();
  return useCallback(
    (options: Parameters<typeof refreshScreenplayParseResultQueries>[1]) =>
      refreshScreenplayParseResultQueries(queryClient, options),
    [queryClient]
  );
}

/**
 * 查询资源关联的分镜列表。
 * 供组件调用，避免直接 import @/services/api/storyboards。
 * @param projectId - 项目 ID
 * @param elementType - 元素类型（数值：1=角色 2=场景 3=道具）
 * @param elementId - 元素 UUID
 * @param tagId - 可选，精确到某个装扮/布景/状态
 */
export function fetchLinkedStoryboards(
  projectId: string,
  elementType: number,
  elementId: string,
  tagId?: string
): Promise<Storyboard[]> {
  return storyboardApi.getLinkedStoryboards(projectId, elementType, elementId, tagId);
}

export function invalidateAllStoryboardQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: storyboardKeys.all });
}

export function removeStoryboardDetailCache(queryClient: QueryClient, storyboardId: string): void {
  queryClient.removeQueries({ queryKey: storyboardKeys.detail(storyboardId) });
}

export function removeStoryboardWindowQueries(queryClient: QueryClient, projectId: string): void {
  queryClient.removeQueries({
    queryKey: [...storyboardKeys.all, 'window', projectId],
  });
}

export function invalidateStoryboardInfinite(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({
    queryKey: [...storyboardKeys.all, 'infinite', projectId],
  });
}

export function invalidateStoryboardWindow(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({
    queryKey: [...storyboardKeys.all, 'window', projectId],
  });
}

export function setStoryboardsListsData(
  queryClient: QueryClient,
  updater: (old: Storyboard[] | undefined) => Storyboard[] | undefined
): void {
  queryClient.setQueriesData<Storyboard[]>({ queryKey: storyboardKeys.lists() }, updater);
}

export function markStoryboardListRootStale(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({
    queryKey: storyboardKeys.listRoot(projectId),
    refetchType: 'none',
  });
}
